<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      salesOrder: null,
      loadError: '',
      document: null,
      documentName: '',
      uploadErrors: {},
      submitted: false,
      alreadyUploaded: false,
    };
  },

  mounted() {
    const public_token = this.$route.params.public_token;
    this.loadSalesOrder(public_token);
  },

  methods: {
    async loadSalesOrder(public_token) {
    this.setLoading(true);
    try {
        const response = await axios.get(`sales-orders/${public_token}`);
        this.salesOrder = response.data.data;
    } catch (error) {
        this.loadError = error.message || 'Sales order not found or link expired.';
    } finally {
        this.setLoading(false);
    }
    },

    handleDocChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.document = file;
        this.documentName = file.name;
      }
    },

    removeDoc() {
      this.document = null;
      this.documentName = '';
      if (this.$refs.driverDocInput) {
        this.$refs.driverDocInput.value = '';
      }
    },

    async submitDocument() {
    this.uploadErrors = {};

    if (!this.document) {
        this.$swalToast('Please select a document to upload', 'warning');
        return;
    }

    this.setLoading(true);
    try {
        const formData = new FormData();
        formData.append('document', this.document);

        await axios.post(
        `sales-orders/driver-attachment/${this.$route.params.public_token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        this.submitted = true;
        } catch (error) {
                if (error.type === 'validation' && error.errors) {
                this.uploadErrors = error.errors;
                const firstMsg = Object.values(error.errors)[0]?.[0];
                this.$swalToast(firstMsg || 'Please fix the errors below', 'error');
                } else {
                const msg = error.message || 'Failed to upload document';
                this.$swalToast(msg, 'error');

                // ← YE NAYA BLOCK ADD KARO
                if (msg.toLowerCase().includes('already uploaded')) {
                this.alreadyUploaded = true;
                }
                }
            } finally {
                this.setLoading(false);
            }
    },
  },
};
</script>

<template>
  <div style="min-height:100vh;background:#f1f5f9;padding:24px 16px;display:flex;align-items:flex-start;justify-content:center;">

    <div style="max-width:560px;width:100%;">

      <!-- Load Error -->
      <div v-if="loadError" 
        style="background:#fff;border-radius:14px;padding:32px 24px;text-align:center;box-shadow:0 2px 10px rgba(0,0,0,.06);">
        <i class="ti ti-alert-triangle" style="font-size:36px;color:#f59e0b;display:block;margin-bottom:10px;"></i>
        <div style="font-size:14px;font-weight:600;color:#0f172a;">{{ loadError }}</div>
      </div>

      <template v-else-if="salesOrder">

        <!-- SO Info -->
        <div style="background:#fff;border-radius:14px;padding:20px 24px;margin-bottom:16px;box-shadow:0 2px 10px rgba(0,0,0,.06);">
          <div style="font-size:11px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;">Sales Order</div>
          <div style="font-size:20px;font-weight:800;color:#0f172a;">{{ salesOrder.so_number }}</div>
          <div style="font-size:13px;color:#64748b;margin-top:3px;" v-if="salesOrder.vehicle_number">
            <i class="ti ti-truck me-1"></i>{{ salesOrder.vehicle_number }}
          </div>
        </div>

        <!-- Already Submitted -->
        <div v-if="submitted"
          style="background:#fff;border-radius:14px;padding:32px 24px;text-align:center;box-shadow:0 2px 10px rgba(0,0,0,.06);">
          <i class="ti ti-circle-check" style="font-size:40px;color:#16a34a;display:block;margin-bottom:10px;"></i>
          <div style="font-size:15px;font-weight:700;color:#16a34a;">Document Uploaded Successfully!</div>
          <div style="font-size:12.5px;color:#64748b;margin-top:6px;">Thank you. Your document has been received.</div>
        </div>

        <!-- Already Uploaded  -->
         <div v-else-if="alreadyUploaded || (salesOrder.driver_document && salesOrder.driver_document.length > 0)"
        style="background:#fff;border-radius:14px;padding:32px 24px;text-align:center;box-shadow:0 2px 10px rgba(0,0,0,.06);">
        <i class="ti ti-circle-check" style="font-size:40px;color:#16a34a;display:block;margin-bottom:10px;"></i>
        <div style="font-size:15px;font-weight:700;color:#16a34a;">You have already uploaded the document</div>
        <div style="font-size:12.5px;color:#64748b;margin-top:6px;">No further action is needed for this delivery.</div>
        </div>

        <!-- Upload Form -->
        <div v-else style="background:#fff;border-radius:14px;padding:20px 24px;box-shadow:0 2px 10px rgba(0,0,0,.06);">
          <div style="font-size:13px;font-weight:700;color:#0f172a;margin-bottom:16px;display:flex;align-items:center;gap:8px;">
            <i class="ti ti-file-upload" style="color:#3b82f6;font-size:16px;"></i>
            Upload Delivery Document
          </div>

          <div v-if="!documentName"
            style="border:2px dashed #e2e8f0;border-radius:10px;padding:32px;text-align:center;cursor:pointer;background:#fafafa;"
            @click="$refs.driverDocInput.click()"
            @dragover.prevent
            @drop.prevent="handleDocChange({ target: { files: $event.dataTransfer.files } })">
            <i class="ti ti-cloud-upload" style="font-size:32px;color:#94a3b8;display:block;margin-bottom:8px;"></i>
            <div style="font-size:13px;font-weight:600;color:#475569;">Click to upload or drag & drop</div>
            <div style="font-size:11px;color:#94a3b8;margin-top:4px;">PDF, JPG, PNG (Max 1MB)</div>
            <input type="file" ref="driverDocInput" style="display:none;" accept=".pdf,.jpg,.jpeg,.png" @change="handleDocChange" />
          </div>

          <div v-else
            style="display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border:1.5px solid #e2e8f0;border-radius:10px;background:#f8fafc;">
            <div style="display:flex;align-items:center;gap:10px;">
              <div style="width:36px;height:36px;background:#eff6ff;border-radius:8px;display:flex;align-items:center;justify-content:center;">
                <i class="ti ti-file-filled" style="font-size:17px;color:#3b82f6;"></i>
              </div>
              <div>
                <div style="font-size:13px;font-weight:600;color:#1e293b;">{{ documentName }}</div>
                <div style="font-size:11px;color:#94a3b8;">Ready to upload</div>
              </div>
            </div>
            <button type="button" @click="removeDoc"
              style="width:30px;height:30px;border-radius:7px;background:#fff0f3;border:1.5px solid #fecdd3;color:#f43f5e;cursor:pointer;">
              <i class="ti ti-trash"></i>
            </button>
          </div>

          <div v-if="uploadErrors.document" style="font-size:11px;color:#f43f5e;margin-top:6px;">
            {{ uploadErrors.document[0] }}
          </div>

          <button type="button" @click="submitDocument"
            style="width:100%;margin-top:16px;padding:11px;background:#16a34a;border:none;border-radius:9px;color:#fff;font-size:13.5px;font-weight:700;cursor:pointer;">
            <i class="ti ti-upload me-1"></i> Submit Document
          </button>
        </div>

      </template>

    </div>
  </div>
</template>